import React, { useEffect, useState } from 'react';
import { AppRemixIcon, AppTextArea, AppNoInfo } from '@/layout/AppComponents';
import styled from 'styled-components';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Accordion } from 'react-bootstrap';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../../../routing/helpers/router';
import Translator from '../../../../../components/helpers/Translator';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import colors from '../../../../../../constants/colors';
import useIsMounted from '../../../../../components/hooks/useIsMounted';

export default function NoAcceptanceTab(props) {
  const { plantObligationID } = props;
  const { moduleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex].permissions
  );
  const noAcceptanceTabs = useSelector((s) => s.currentPage?.noAcceptanceTabs);
  const noAcceptance = useSelector(
    (s) => s.currentPage?.noAcceptanceTabs?.[plantObligationID]
  );
  const poLevel3 = useSelector(
    (s) => s.currentPage?.plantObligationLevels?.level3
  );
  const [isLoading, setIsLoading] = useState(false);

  // > LOAD INFORMATION TAB
  useEffect(() => {
    if (!noAcceptance) {
      setIsLoading(true);
      (async () => {
        try {
          const response = await axios.post(
            axiosPath('api_r_user_module_legalCompliance_noAcceptance_tab'),
            { moduleBridgeHex, plantHex, plantObligationID }
          );
          if (isMounted.current) {
            dispatch(
              currentPageAction.change('noAcceptanceTabs', {
                [plantObligationID]: response.data,
              })
            );
            setIsLoading(false);
          }
        } catch (err) {
          console.log(err);
        }
      })();
    }
  }, []);
  // < LOAD INFORMATION TAB

  // > EDIT
  const handleEdit = async (values) => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_legalCompliance_noAcceptance_edit'),
        {
          moduleBridgeHex,
          plantHex,
          plantObligationID,
          ...values,
        }
      );
      if (isMounted.current) {
        dispatch(
          currentPageAction.change(
            'noAcceptanceTabs',
            {
              ...noAcceptanceTabs,
              [plantObligationID]: {
                ...noAcceptanceTabs?.[plantObligationID],
                ...response.data.tabData,
              },
            },
            true
          )
        );
        dispatch(
          currentPageAction.change('plantObligationLevels', {
            level3: {
              ...poLevel3,
              [plantObligationID]: {
                ...poLevel3?.[plantObligationID],
                alerts: {
                  ...poLevel3?.[plantObligationID].alerts,
                  apiLock: response.data.apiLock,
                },
              },
            },
          })
        );
      }
    } catch (err) {}
  };
  // < EDIT

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    description: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
    confirmExport: Yup.bool().required().oneOf([true]),
  });
  // < FORM SCHEMA

  if (isLoading) return <AppNoInfo minHeight={300} />;

  return (
    <TabContainer>
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12 mb-2">
            <Translator
              file="module-legalCompliance"
              name="module.lc.export.no.consent"
            />
          </div>
        </div>
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
          validationSchema={validationSchema}
          initialValues={{
            description: noAcceptance?.description,
            confirmExport: false,
          }}
          onSubmit={handleEdit}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <div className="row">
              <div className="col-lg-12">
                <AppTextArea
                  title={
                    <Translator
                      file="module-legalCompliance"
                      name="module.lc.evidence.description"
                    />
                  }
                  icon={<AppRemixIcon icon="align-left" noVariants />}
                  value={values.description}
                  onChange={(v) => setFieldValue('description', v)}
                  error={errors.description}
                  isReadOnly={
                    noAcceptance?.hasPlantObligationApiQueues &&
                    !permissions?.gcl?.module?.api?.send?.noBlock
                  }
                />
              </div>
              {(!noAcceptance?.hasPlantObligationApiQueues ||
                permissions?.gcl?.module?.api?.send?.noBlock) && (
                <div className="col-lg-12">
                  <label
                    className={`col-form-label mr-3 pointer${
                      errors.confirmExport ? ' text-danger' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mr-1"
                      value={values.confirmExport}
                      onChange={() =>
                        setFieldValue('confirmExport', !values.confirmExport)
                      }
                      checked={values.confirmExport}
                      disabled={
                        noAcceptance?.hasPlantObligationApiQueues &&
                        !permissions?.gcl?.module?.api?.send?.noBlock
                      }
                    />
                    <Translator
                      file="module-legalCompliance"
                      name="confirm.export"
                    />
                  </label>
                </div>
              )}
              {(!noAcceptance?.hasPlantObligationApiQueues ||
                permissions?.gcl?.module?.api?.send?.noBlock) && (
                <div className="col-lg-12">
                  <span
                    className="btn btn-primary btn-sm mt-3"
                    onClick={() => (isSubmitting ? undefined : handleSubmit())}
                    disabled={isSubmitting}
                  >
                    <Translator
                      file="module-legalCompliance"
                      name={
                        noAcceptance?.hasPlantObligationApiQueues &&
                        permissions?.gcl?.module?.api?.send?.noBlock
                          ? 'replace.n.c'
                          : 'module.lc.export.n.c'
                      }
                    />
                  </span>
                </div>
              )}
            </div>
          )}
        </Formik>
        {!permissions?.gcl?.module?.api?.send?.noBlock && (
          <div className="row">
            <div className="col-lg-12 mt-3">
              <p className="text-danger">
                <Translator
                  file="module-legalCompliance"
                  name={
                    noAcceptance?.hasPlantObligationApiQueues
                      ? 'module.lc.info.exported.obligation.unblocked'
                      : 'module.lc.export.n.c.info.sent.text'
                  }
                  isHtml
                />
              </p>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-lg-12">
            <Accordion>
              {noAcceptance?.apiLogs?.map((al) => (
                <Container key={al.id} className="mt-3">
                  <Accordion.Item eventKey={al.id?.toString()}>
                    <Accordion.Header
                      as="h3"
                      className="panel-title pointer m-0"
                    >
                      {/* <h3 className="panel-title pointer m-0"> */}
                      <Translator
                        file="module-legalCompliance"
                        name="module.lc.creation"
                      />
                      : {al.startDateStamp} -{' '}
                      <Translator
                        file="module-legalCompliance"
                        name="module.lc.closing"
                      />
                      : {al.endDateStamp}
                      {/* </h3> */}
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="mt-2">{al.description}</div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Container>
              ))}
            </Accordion>
          </div>
        </div>
        {noAcceptance?.apiQueueLogs?.length > 0 && (
        <div className="row">
          <div className="col-lg-12">
            <h3 className="panel-title mt-4">Histórico</h3>
            <Accordion>
              {noAcceptance?.apiQueueLogs?.map((aq) => (
                <Container key={aq.id} className="mt-3">
                  <Accordion.Item eventKey={aq.id?.toString()}>
                    <Accordion.Header
                      as="h3"
                      className="panel-title pointer m-0"
                    >
                      {aq.type === 0 && (
                        <>
                          <AppRemixIcon icon="upload" title="Enviado" />{` - ${aq.dateStamp} - ${aq.user}`}
                        </>
                      )}
                      {aq.type === 1 && (
                        <>
                          <AppRemixIcon icon="download" title="Recibido" />{` - ${aq.dateStamp} - ${aq.apiUser}`}
                        </>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      {aq.type === 0 && (<>
                        <h3 className="panel-title mt-2">Descripción</h3>
                        <div className="mt-2">{aq.description}</div>
                      </>
                        
                      )}
                      {aq.type === 1 && (
                       <>
                       <h3 className="panel-title mt-2">Comentario</h3>
                       <div className="mt-2">{aq.apiComment}</div>
                     </>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                </Container>
              ))}
              </Accordion>
            </div>
          </div>
        )}
      </div>
    </TabContainer>
  );
}

const TabContainer = styled.div`
  height: 367px;
  overflow-y: auto;
`;

const Container = styled.div`
  border: 1px solid ${colors.grey300};
  margin-bottom: 5px;
  padding: 10px;

  .accordion-item{
    &>.panel-title>button{
      background-color: white;
      border: none;
      padding: 0;
      width: 100%;
      text-align: left;
      font-weight: bold;
      color: ${colors.primary};
    }
  }
`;
